<template>
    <div class="results">
        <h2>Results</h2>
        <!-- FAVORITES -->
        <span v-if="favorites.length" style="display: inline-block; border: 1px solid black; padding: 20px;">
                {{ favorites.length }} {{ favorites.length === 1 ? 'property' : 'properties' }} pinned.
                <ul>
                    <li v-for="favorite in favorites">{{ favorite.name }}</li>
                </ul>
                <button
                    v-text="'Send these to a friend'"
                    @click="emailRecipientType = 'friend'; showSendFavorites = true;"
                />
                <button
                    v-text="'Send these to an agent'"
                    @click="emailRecipientType = 'agent'; showSendFavorites = true;"
                />
            </span>

        <!-- RESULTS -->
        <search-pagination
            :pagination="pagination"
            @page="$emit('page', $event)"
        />

        <template v-if="pagination.hasOwnProperty('TotalRows') && pagination.TotalRows > 0">
            <property-summary
                v-for="property in results"
                :property="property"
                :loading="loading"
                :key="property.Id"
                @toggle-hide="$emit('toggle-hide', property)"
                @toggle-favorite="$emit('toggle-favorite', property)"
                @request-reservation=""
            />
        </template>
        <template v-else-if="!pagination.hasOwnProperty('TotalRows') || loading">
            <property-summary
                v-for="i in 5"
                :property="null"
                :key="i"
            />
        </template>
        <div v-else class="no-results">No results match your search criteria.</div>

        <search-pagination
            :pagination="pagination"
            @page="$emit('page', $event)"
        />

        <!-- SUPPRESSIONS -->
        <template v-if="suppressions.length">
            <template v-if="showSuppressions">
                <ul>
                    <li v-for="property in suppressions">
                        {{ property.name }}
                        <span @click="suppressionsRemove(property)">Unhide this property</span>
                    </li>
                </ul>
                <span @click="showSuppressions = false">Hide summary</span>
            </template>
            <div v-else>
                {{ suppressions.length }} propert{{ suppressions.length === 1 ? 'y' : 'ies'}} hidden.
                <span @click="showSuppressions = true">Click to show summary</span>
            </div>
        </template>

        <send-favorites
            v-if="showSendFavorites"
            :recipient-type="emailRecipientType"
            :favorites="favorites"
            @sent="showSendFavorites = false"
            @cancel="showSendFavorites = false"
        />

        <request-reservation
            v-if="showRequestReservation"
            :property="currentProperty"
            @sent="showRequestReservation = false"
            @cancel="showRequestReservation = false"
        />
    </div>
</template>

<script>

import PropertySummary from './PropertySummary';
import RequestReservation from './RequestReservation';
import SearchPagination from './SearchPagination';
import SendFavorites from './SendFavorites';

export default {
    components: {
        PropertySummary,
        RequestReservation,
        SearchPagination,
        SendFavorites,
    },
    props: {
        results: {
            type: Array,
            default() {
                return [];
            },
        },
        pagination: {
            type: Object,
            default() {
                return {};
            },
        },
        loading: {
            type: Boolean,
            default: true,
        },
    },
    data: function() {
        return {
            myResults: [],
            notables: [],
            showSuppressions: false,

            emailRecipientType: null,
            showSendFavorites: false,

            currentProperty: null,
            showRequestReservation: false,
        }
    },
    computed: {
        resultsToShow() {
            return this.myResults.filter(property => !property.isSuppressed);
        },
        favorites() {
            return this.notables.filter(property => property.isFavorite);
        },
        suppressions() {
            return this.notables.filter(property => property.isSuppressed);
        },
    },
    watch: {
        results: {
            deep: true,
            handler() {
                this.myResults.splice(0, this.myResults.length, ...this.results.map(property => {
                    let notable = this.notables.find(notable => notable.slug === property.slug);
                    property.isFavorite = !!notable && notable.isFavorite;
                    property.isSuppressed = !!notable && notable.isSuppressed;
                    property.notes = (notable && notable.notes) ?? '';
                    return Object.assign({}, property);
                }));
            },
        }
    },
    methods: {
        favoritesAdd(property) {
            this.updateNotables(property, 'isFavorite', true);
        },
        favoritesRemove(property) {
            this.updateNotables(property, 'isFavorite', false);
        },
        isFavorite(property) {
            return this.notables.some(notable => notable.slug === property.slug && notable.isFavorite);
        },

        suppressionsAdd(property) {
            this.updateNotables(property, 'isSuppressed', true);
            this.updateNotables(property, 'isFavorite', false);
        },
        suppressionsRemove(property) {
            this.updateNotables(property, 'isSuppressed', false);
            this.showSuppressions &= this.suppressions.length;
        },
        isSuppressed(property) {
            return this.notables.some(notable => notable.slug === property.slug && notable.isSuppressed);
        },

        updateNotes(property) {
            this.updateNotables(property, 'notes', property.notes);
        },

        updateNotables(property, objProp, value) {
            property[objProp] = value;
            let index = this.notables.findIndex(notable => notable.slug === property.slug);
            if (index >= 0) {
                this.notables[index][objProp] = value;
            } else {
                this.notables.push(property);
            }
        },
    },
}
</script>

<style lang="scss">
.results {
    margin-bottom: 80px;
    .property {
        border: 1px solid black;
        padding: 20px;
        margin: 10px 30px;

        .name {
            font-weight: bold;
        }
    }
}
</style>
